import { useEffect, useRef, useState } from 'react'
import {CheckCircle, XCircle, Star, Shield, Clock, Award, BookOpen, Plane, Globe, ChevronDown, ChevronRight, Zap, Users, TrendingUp, Lock} from 'lucide-react'

// ─── Countdown Timer ─────────────────────────────────────────────────────────
function useCountdown() {
  const [time, setTime] = useState({ h: 5, m: 47, s: 23 })
  useEffect(() => {
    const id = setInterval(() => {
      setTime(prev => {
        let { h, m, s } = prev
        s--
        if (s < 0) { s = 59; m-- }
        if (m < 0) { m = 59; h-- }
        if (h < 0) { h = 23; m = 59; s = 59 }
        return { h, m, s }
      })
    }, 1000)
    return () => clearInterval(id)
  }, [])
  return time
}

// ─── Intersection Observer for scroll animations ──────────────────────────────
function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible') },
      { threshold: 0.15 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return ref
}

function ScrollReveal({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.animationDelay = `${delay}ms`
          el.classList.add('visible')
        }
      },
      { threshold: 0.1 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [delay])
  return <div ref={ref} className={`obsv ${className}`}>{children}</div>
}

// ─── Gold Badge / Seal ────────────────────────────────────────────────────────
function GoldSeal() {
  return (
    <div className="relative inline-flex items-center justify-center w-36 h-36 md:w-44 md:h-44">
      <svg viewBox="0 0 160 160" className="absolute inset-0 w-full h-full animate-float" style={{ filter: 'drop-shadow(0 0 18px rgba(201,168,76,0.7))' }}>
        {Array.from({ length: 16 }).map((_, i) => {
          const angle = (i / 16) * 360
          const x1 = 80 + 70 * Math.cos((angle - 90) * Math.PI / 180)
          const y1 = 80 + 70 * Math.sin((angle - 90) * Math.PI / 180)
          const x2 = 80 + 60 * Math.cos((angle - 90) * Math.PI / 180)
          const y2 = 80 + 60 * Math.sin((angle - 90) * Math.PI / 180)
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#c9a84c" strokeWidth="3" />
        })}
        <circle cx="80" cy="80" r="58" fill="url(#sealGrad)" stroke="#c9a84c" strokeWidth="2" />
        <circle cx="80" cy="80" r="52" fill="none" stroke="#f5d98b" strokeWidth="1" opacity="0.5" />
        <defs>
          <radialGradient id="sealGrad" cx="50%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#1e3060" />
            <stop offset="100%" stopColor="#0a1628" />
          </radialGradient>
        </defs>
      </svg>
      <div className="relative z-10 text-center px-2">
        <p className="text-xs font-black tracking-widest text-amber-300 leading-tight uppercase">E-BOOK</p>
        <p className="text-sm font-black tracking-widest text-amber-300 leading-tight uppercase">PREMIUM</p>
        <div className="w-10 h-0.5 bg-amber-400 mx-auto my-1" />
        <p className="text-xs font-bold tracking-widest text-amber-200 leading-tight uppercase">GUIA</p>
        <p className="text-xs font-bold tracking-widest text-amber-200 leading-tight uppercase">COMPLETO</p>
        <div className="w-10 h-0.5 bg-amber-400 mx-auto my-1" />
        <p className="text-xs font-bold tracking-widest text-amber-300 leading-tight uppercase" style={{ fontSize: '8px' }}>PASSO A PASSO</p>
        <p className="text-xs font-bold tracking-widest text-amber-300 leading-tight uppercase" style={{ fontSize: '8px' }}>OFICIAL</p>
      </div>
    </div>
  )
}

// ─── APPROVED Stamp ───────────────────────────────────────────────────────────
function ApprovedStamp() {
  return (
    <div
      className="inline-block border-4 border-green-400 rounded px-4 py-2 rotate-[-8deg]"
      style={{
        boxShadow: '0 0 20px rgba(74, 222, 128, 0.5)',
        background: 'rgba(0,40,0,0.3)',
      }}
    >
      <span
        className="text-green-400 font-black text-2xl md:text-3xl tracking-widest uppercase"
        style={{ fontFamily: 'Montserrat, sans-serif', textShadow: '0 0 12px rgba(74,222,128,0.8)' }}
      >
        ✓ APPROVED
      </span>
    </div>
  )
}

// ─── Main Home Component ──────────────────────────────────────────────────────
export default function Home() {
  const countdown = useCountdown()
  const heroRef = useRef<HTMLDivElement>(null)
  const [heroLoaded, setHeroLoaded] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const pad = (n: number) => String(n).padStart(2, '0')

  // Dispara evento de conversao do Meta Pixel e abre o checkout
  const handleCheckout = () => {
    const fbq = (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq
    if (typeof fbq === 'function') {
      fbq('track', 'InitiateCheckout', { value: 49.9, currency: 'BRL', content_name: 'O Guia Completo do Visto Americano' })
    }
    window.open('https://pay.hotmart.com/W105991591U?checkoutMode=10', '_blank')
  }

  const learnItems = [
    { icon: '🛂', title: 'Como tirar o passaporte', desc: 'Passo a passo completo para emitir seu passaporte sem complicações' },
    { icon: '📋', title: 'Como preencher o DS-160', desc: 'Orientações detalhadas para não errar no formulário mais importante' },
    { icon: '💳', title: 'Como pagar as taxas', desc: 'Todos os valores, como pagar e onde pagar corretamente' },
    { icon: '📅', title: 'Como agendar a entrevista', desc: 'Sistema oficial explicado do zero, sem erros' },
    { icon: '🎤', title: 'O que falar no consulado', desc: 'Scripts e estratégias para se comunicar com o oficial' },
    { icon: '❓', title: 'Perguntas que o oficial faz', desc: 'Lista completa com as respostas ideais para cada pergunta' },
    { icon: '📈', title: 'Como aumentar suas chances', desc: 'Fatores decisivos que fazem a diferença na aprovação' },
    { icon: '⚠️', title: 'Como evitar erros fatais', desc: 'Os principais motivos de negativa e como evitá-los' },
    { icon: '🗽', title: 'Como funciona a imigração', desc: 'Entenda o sistema americano e o que os oficiais avaliam' },
    { icon: '📦', title: 'Como receber o visto em casa', desc: 'O que acontece após a aprovação e como receber seu passaporte' },
  ]

  const objections = [
    { icon: '✈️', title: '"Nunca viajei para fora do país"', response: 'Isso não é impedimento! O guia foi criado exatamente para quem está fazendo isso pela primeira vez. Você vai aprender tudo do zero, sem precisar de experiência anterior.' },
    { icon: '🗣️', title: '"Não sei falar inglês"', response: 'A entrevista pode ser feita em português. O guia te ensina o que falar e como se comunicar mesmo com conhecimento básico de inglês.' },
    { icon: '😰', title: '"Tenho medo de ser negado"', response: 'O medo vem da falta de preparo. Com o guia, você vai saber exatamente o que fazer e falar, reduzindo drasticamente as chances de negativa.' },
    { icon: '😕', title: '"Não entendo nada do processo"', response: 'O guia foi escrito em linguagem simples e direta. Sem burocracia, sem termos técnicos. Você vai entender tudo.' },
    { icon: '💸', title: '"Achei que precisava de consultoria cara"', response: 'Consultorias cobram de R$4.000 a R$5.000. Nosso guia entrega praticamente as mesmas informações por apenas R$49,90.' },
  ]

  const testimonials = [
    { name: 'Carlos Henrique', city: 'São Paulo, SP', stars: 5, text: 'Eu tinha muito medo da entrevista. Depois do guia fui muito mais preparado e consegui meu visto na primeira tentativa. Valeu cada centavo!', initials: 'CH' },
    { name: 'Juliana Souza', city: 'Belo Horizonte, MG', stars: 5, text: 'Paguei R$49,90 e economizei milhares em consultoria. As informações são completas, claras e me deram muita segurança. Hoje estou planejando minha viagem a Orlando!', initials: 'JS' },
    { name: 'Ricardo Almeida', city: 'Rio de Janeiro, RJ', stars: 5, text: 'Segui o guia à risca. Em menos de 2 meses tirei o passaporte, agendei a entrevista e fui aprovado. Simplesmente incrível!', initials: 'RA' },
    { name: 'Fernanda Costa', city: 'Curitiba, PR', stars: 5, text: 'Nunca tinha viajado para fora do Brasil. Com o guia me senti super preparada. O oficial até elogiou minha organização durante a entrevista.', initials: 'FC' },
    { name: 'Marcos Oliveira', city: 'Salvador, BA', stars: 5, text: 'Tentei sozinho antes e fui negado. Comprei o guia, entendi onde errei e na segunda tentativa fui aprovado. Material muito completo!', initials: 'MO' },
    { name: 'Ana Beatriz Lima', city: 'Fortaleza, CE', stars: 5, text: 'O guia é muito mais completo do que eu esperava. Cobre tudo desde o passaporte até como receber o visto em casa. Recomendo demais!', initials: 'AB' },
  ]

  const benefits = [
    { img: 'https://static.lumi.new/90/9074e4af8d0453a14591cfe6651f1f25.png', title: 'Realizar o Sonho Americano', desc: 'Nova York, Orlando, Miami — esses destinos podem ser seus. O visto é só o primeiro passo.' },
    { img: 'https://static.lumi.new/6b/6bfda9db15464c494c373d5d31b2c75a.png', title: 'Viajar com Segurança', desc: 'Com o passaporte carimbado, você cruza a fronteira americana com total tranquilidade.' },
    { img: 'https://static.lumi.new/b8/b816ec04122237a64eab964cc4db6bef.png', title: 'Confiança na Entrevista', desc: 'Entre no consulado sabendo exatamente o que falar. Sem nervosismo, sem surpresas.' },
    { img: 'https://static.lumi.new/7b/7b190a6fc8929522b02203dd7e606ea0.png', title: 'Nova York à Noite', desc: 'Imagine-se caminhando pela Times Square. Esse momento começa com o processo certo.' },
  ]

  return (
    <div className="min-h-screen bg-[#0a1628] font-['Montserrat']">
      {/* ── TOP BAR ── */}
      <div className="bg-green-700 py-2 text-center text-white text-sm font-bold px-4">
        🔥 OFERTA POR TEMPO LIMITADO — Valor promocional encerrando em breve!
      </div>

      {/* ── HERO SECTION ── */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden" style={{ overflowX: 'hidden' }}>
        {/* Hero Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.lumi.new/75/7540cff7c50f17a2e66b5cf19a2e642f.png"
            alt="Hero Background"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.55) saturate(1.2)' }}
          />
          <div className="hero-bg absolute inset-0" />
          {/* Particle glow overlay */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.12) 0%, transparent 60%)'
          }} />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center py-20 md:py-28">
          {/* Bestseller Badge */}
          <div
            className={`flex justify-center mb-5 transition-all duration-700 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
            style={{ transitionDelay: '0s' }}
          >
            <div
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-black uppercase tracking-widest"
              style={{
                background: 'linear-gradient(90deg, #9d7a2e 0%, #c9a84c 20%, #f5d98b 50%, #c9a84c 80%, #9d7a2e 100%)',
                backgroundSize: '200% auto',
                boxShadow: '0 0 24px rgba(201,168,76,0.6), 0 2px 12px rgba(0,0,0,0.5)',
                color: '#0a1628',
                animation: 'shimmerBadge 2.5s linear infinite',
              }}
            >
              <span style={{ fontSize: '16px' }}>🏆</span>
              <span>O Ebook Mais Vendido do Brasil</span>
              <span style={{ fontSize: '16px' }}>🏆</span>
            </div>
          </div>

          {/* Flag line */}
          <div
            className={`flex items-center justify-center gap-3 mb-8 transition-all duration-1000 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.1s' }}
          >
            <div className="flag-wave text-3xl md:text-4xl">🇺🇸</div>
            <div className="h-px flex-1 max-w-24 bg-gradient-to-r from-transparent to-amber-400/50" />
            <p className="text-amber-300 text-xs md:text-sm font-bold tracking-[0.25em] uppercase">O Guia Definitivo do Visto Americano</p>
            <div className="h-px flex-1 max-w-24 bg-gradient-to-l from-transparent to-amber-400/50" />
            <div className="flag-wave text-3xl md:text-4xl" style={{ animationDelay: '0.5s' }}>🇺🇸</div>
          </div>

          {/* ── HERO MOCKUP PREMIUM ── */}
          <div
            className={`flex justify-center mb-10 transition-all duration-1000 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            style={{ transitionDelay: '0.25s' }}
          >
            {/* Outer glow aura */}
            <div className="relative" style={{ perspective: '1200px' }}>

              {/* Ambient radial glow behind everything */}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: 'radial-gradient(ellipse 80% 60% at 50% 60%, rgba(201,168,76,0.35) 0%, rgba(34,197,94,0.08) 40%, transparent 70%)',
                transform: 'scale(1.6)',
                filter: 'blur(28px)',
                zIndex: 0,
              }} />

              {/* ── Book 3D Scene ── */}
              <div className="relative z-10 flex items-end justify-center gap-0" style={{ transformStyle: 'preserve-3d' }}>

                {/* Book spine (left side) */}
                <div
                  className="flex-shrink-0"
                  style={{
                    width: '22px',
                    height: 'clamp(220px, 42vw, 330px)',
                    background: 'linear-gradient(180deg, #c9a84c 0%, #9d7a2e 40%, #7a5e22 100%)',
                    borderRadius: '4px 0 0 4px',
                    boxShadow: '-6px 0 20px rgba(0,0,0,0.8)',
                    transform: 'rotateY(60deg) translateX(4px)',
                    transformOrigin: 'right center',
                  }}
                >
                  <div style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    color: '#0a1628',
                    fontSize: '8px',
                    fontWeight: 900,
                    fontFamily: 'Montserrat, sans-serif',
                    letterSpacing: '0.12em',
                    padding: '12px 4px',
                    opacity: 0.85,
                  }}>GUIA DO VISTO AMERICANO</div>
                </div>

                {/* Book front cover */}
                <div
                  className="relative flex-shrink-0 animate-float"
                  style={{
                    width: 'clamp(180px, 36vw, 280px)',
                    borderRadius: '0 10px 10px 0',
                    overflow: 'hidden',
                    transform: 'rotateY(-8deg)',
                    transformOrigin: 'left center',
                    boxShadow: '12px 20px 60px rgba(0,0,0,0.8), 0 0 70px rgba(201,168,76,0.4), 2px 0 0 rgba(201,168,76,0.2)',
                    border: '1.5px solid rgba(201,168,76,0.5)',
                    borderLeft: 'none',
                  }}
                >
                  {/* Actual cover image */}
                  <img
                    src="https://static.lumi.new/17/17df7e163d32a7a35cc13d1b89432343.webp"
                    alt="O Guia Completo do Visto Americano"
                    className="w-full h-auto block"
                    style={{ filter: 'brightness(1.08) saturate(1.15) contrast(1.02)', display: 'block' }}
                  />
                  {/* Gloss shine sweep */}
                  <div className="absolute inset-0 pointer-events-none" style={{
                    background: 'linear-gradient(115deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 30%, transparent 55%, rgba(255,255,255,0.03) 100%)',
                  }} />
                  {/* Bottom gold rim light */}
                  <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.6), transparent)' }} />
                </div>

                {/* APPROVED stamp — floated over cover, bottom-right */}
                <div
                  className="absolute animate-stamp"
                  style={{
                    bottom: 'clamp(-10px, -2vw, -16px)',
                    right: 'clamp(-30px, -6vw, -44px)',
                    zIndex: 20,
                  }}
                >
                  <div style={{
                    background: 'rgba(0, 18, 0, 0.55)',
                    border: '4px solid #22c55e',
                    borderRadius: '10px',
                    padding: 'clamp(8px,1.5vw,12px) clamp(16px,3vw,26px)',
                    transform: 'rotate(-12deg)',
                    boxShadow: '0 0 32px rgba(34,197,94,0.8), 0 0 80px rgba(34,197,94,0.25), 0 6px 24px rgba(0,0,0,0.7)',
                    backdropFilter: 'blur(4px)',
                  }}>
                    <div style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 900,
                      fontSize: 'clamp(18px, 4vw, 28px)',
                      color: '#22c55e',
                      letterSpacing: '0.18em',
                      textShadow: '0 0 20px rgba(34,197,94,1), 0 0 40px rgba(34,197,94,0.5)',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}>✓ APPROVED</div>
                    <div style={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 700,
                      fontSize: 'clamp(9px, 1.6vw, 13px)',
                      color: '#86efac',
                      letterSpacing: '0.28em',
                      textAlign: 'center',
                      marginTop: '4px',
                      textShadow: '0 0 10px rgba(134,239,172,0.6)',
                    }}>US VISA ★★★</div>
                  </div>
                </div>

                {/* Gold stars above cover */}
                <div className="absolute" style={{ top: '-18px', left: '50%', transform: 'translateX(-40%)', zIndex: 15 }}>
                  <span style={{ color: '#f5d98b', fontSize: '15px', letterSpacing: '5px', textShadow: '0 0 12px rgba(245,217,139,0.9), 0 0 24px rgba(201,168,76,0.5)' }}>★★★★★</span>
                </div>

                {/* Floating golden particles */}
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full pointer-events-none"
                    style={{
                      width: i % 2 === 0 ? '6px' : '4px',
                      height: i % 2 === 0 ? '6px' : '4px',
                      background: i % 3 === 0 ? '#f5d98b' : '#c9a84c',
                      top: `${10 + i * 14}%`,
                      left: i % 2 === 0 ? `-${18 + i * 4}px` : 'auto',
                      right: i % 2 !== 0 ? `-${14 + i * 3}px` : 'auto',
                      boxShadow: '0 0 8px rgba(245,217,139,0.9)',
                      animation: `floatY ${2.5 + i * 0.4}s ease-in-out ${i * 0.3}s infinite`,
                      opacity: 0.7,
                    }}
                  />
                ))}
              </div>

              {/* Bottom reflection / shadow */}
              <div className="absolute -bottom-4 left-1/2" style={{
                transform: 'translateX(-50%)',
                width: '70%',
                height: '20px',
                background: 'radial-gradient(ellipse, rgba(201,168,76,0.3) 0%, transparent 70%)',
                filter: 'blur(8px)',
                zIndex: 0,
              }} />
            </div>
          </div>

          {/* Headline */}
          <h1
            className={`font-black leading-tight mb-6 transition-all duration-1000 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(1.6rem, 4.5vw, 3rem)',
              lineHeight: '1.2',
              transitionDelay: '0.4s',
              color: '#ffffff',
              textShadow: '0 2px 30px rgba(0,0,0,0.8)'
            }}
          >
            Descubra o passo a passo que já ajudou{' '}
            <span className="gold-shimmer">milhares de brasileiros</span>{' '}
            a conquistar o visto americano{' '}
            <em style={{ fontFamily: 'Playfair Display, serif' }}>mesmo sem falar inglês</em>{' '}
            ou nunca ter viajado para fora do país.
          </h1>

          {/* Sub-headline */}
          <p
            className={`text-gray-200 text-base md:text-xl font-light leading-relaxed mb-10 max-w-3xl mx-auto transition-all duration-1000 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.6s', textShadow: '0 1px 10px rgba(0,0,0,0.7)' }}
          >
            Enquanto consultorias cobram de <strong className="text-white">R$4.000 a R$5.000</strong> para orientar o processo,{' '}
            este guia entrega praticamente tudo o que você precisa por apenas{' '}
            <strong className="text-amber-300">R$49,90</strong>.
          </p>

          {/* Price Box */}
          <div
            className={`inline-block mb-8 transition-all duration-700 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.7s' }}
          >
            <div
              className="rounded-xl px-6 py-4 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(10,22,40,0.95) 0%, rgba(20,36,68,0.95) 100%)',
                border: '2px solid #c9a84c',
                boxShadow: '0 0 30px rgba(201,168,76,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
              }}
            >
              <p className="text-gray-400 text-sm line-through mb-1">De R$297,00</p>
              <p className="font-black text-3xl md:text-4xl" style={{ color: '#f5d98b', textShadow: '0 0 20px rgba(245,217,139,0.5)' }}>
                por apenas R$49,90
              </p>
              <p className="text-green-400 text-xs font-bold mt-1 tracking-wider">✓ ACESSO IMEDIATO AO DOWNLOAD</p>
            </div>
          </div>

          {/* CTA Button */}
          <div
            className={`flex flex-col items-center gap-4 transition-all duration-700 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '0.9s' }}
          >
            <button
              className="btn-gold rounded-xl text-lg md:text-xl font-black px-8 md:px-14 py-5 md:py-6 uppercase tracking-wider w-full sm:w-auto animate-pulse-gold"
              onClick={handleCheckout}
            >
              🔒 QUERO GARANTIR MEU GUIA AGORA
            </button>
            <p className="text-gray-400 text-xs">
              🔐 Pagamento seguro · Acesso imediato · Garantia de 7 dias
            </p>
          </div>

          {/* Countdown */}
          <div className="mt-10 flex flex-col items-center gap-3">
            <p className="text-amber-300 text-sm font-bold uppercase tracking-widest">
              ⚡ Oferta encerrando em:
            </p>
            <div className="flex gap-3">
              {[{ v: pad(countdown.h), l: 'Horas' }, { v: pad(countdown.m), l: 'Min' }, { v: pad(countdown.s), l: 'Seg' }].map(({ v, l }) => (
                <div key={l} className="text-center">
                  <div
                    className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center text-2xl md:text-3xl font-black text-white"
                    style={{ background: 'rgba(201,168,76,0.15)', border: '2px solid rgba(201,168,76,0.5)' }}
                  >
                    {v}
                  </div>
                  <p className="text-gray-400 text-xs mt-1">{l}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="mt-12 flex justify-center">
            <div className="text-amber-400 animate-bounce">
              <ChevronDown size={28} />
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION: O QUE VOCÊ VAI APRENDER ── */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#0a1628] to-[#0d1f3c]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <ScrollReveal>
            <div className="text-center mb-4">
              <span className="text-amber-400 text-xs font-bold tracking-[0.3em] uppercase">Conteúdo Completo</span>
            </div>
            <h2
              className="text-center font-black mb-4 text-3xl md:text-5xl text-white"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              O Que Você Vai <span className="gold-shimmer">Aprender</span>
            </h2>
            <div className="section-divider" />
            <p className="text-center text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-12">
              Um guia completo, do zero ao visto, sem deixar nenhuma dúvida sem resposta.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-12">
            {learnItems.map((item, i) => (
              <ScrollReveal key={i} delay={i * 50}>
                <div className="card-premium rounded-xl p-5 h-full">
                  <div className="text-3xl mb-3">{item.icon}</div>
                  <h3 className="text-amber-300 font-bold text-sm mb-2 leading-tight">{item.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Highlight */}
          <ScrollReveal>
            <div
              className="rounded-2xl p-6 md:p-10 text-center mx-auto max-w-3xl"
              style={{
                background: 'linear-gradient(135deg, rgba(201,168,76,0.12) 0%, rgba(201,168,76,0.05) 100%)',
                border: '2px solid rgba(201,168,76,0.4)',
                boxShadow: '0 0 40px rgba(201,168,76,0.1)'
              }}
            >
              <BookOpen className="mx-auto mb-4 text-amber-400" size={36} />
              <p
                className="font-black text-xl md:text-3xl text-white"
                style={{ fontFamily: 'Playfair Display, serif', lineHeight: '1.4' }}
              >
                "Você vai entrar na entrevista{' '}
                <span className="gold-shimmer">sabendo exatamente</span>{' '}
                o que falar."
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── SECTION: QUEBRA DE OBJEÇÕES ── */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#0d1f3c] to-[#0a1628]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <ScrollReveal>
            <div className="text-center mb-4">
              <span className="text-amber-400 text-xs font-bold tracking-[0.3em] uppercase">Criado Para Iniciantes</span>
            </div>
            <h2
              className="text-center font-black mb-4 text-3xl md:text-5xl text-white"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Esqueça Seus <span className="gold-shimmer">Medos</span>
            </h2>
            <div className="section-divider" />
            <p className="text-center text-gray-300 text-lg max-w-xl mx-auto mb-12">
              Se você já pensou alguma dessas coisas, este guia foi feito especialmente para você.
            </p>
          </ScrollReveal>

          <div className="space-y-4">
            {objections.map((obj, i) => (
              <ScrollReveal key={i} delay={i * 80}>
                <div
                  className="rounded-xl p-6 md:p-8"
                  style={{
                    background: 'linear-gradient(135deg, rgba(14,28,52,0.9), rgba(10,22,40,0.95))',
                    border: '1px solid rgba(201,168,76,0.2)',
                  }}
                >
                  <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <span className="text-4xl">{obj.icon}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-400 text-base md:text-lg font-semibold mb-3 italic">{obj.title}</p>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={18} />
                        <p className="text-white text-sm md:text-base leading-relaxed">{obj.response}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION: COMPARAÇÃO ── */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#0a1628] via-[#0d1f3c] to-[#0a1628]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <ScrollReveal>
            <div className="text-center mb-4">
              <span className="text-amber-400 text-xs font-bold tracking-[0.3em] uppercase">A Verdade</span>
            </div>
            <h2
              className="text-center font-black mb-4 text-3xl md:text-5xl text-white"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Consultoria vs. <span className="gold-shimmer">E-Book</span>
            </h2>
            <div className="section-divider" />
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* Consultoria */}
            <ScrollReveal delay={100}>
              <div className="rounded-2xl overflow-hidden h-full">
                <div className="bg-gray-800 px-6 py-4 text-center">
                  <h3 className="text-gray-300 font-black text-xl uppercase tracking-wider">Consultoria</h3>
                </div>
                <div className="bg-gray-900 p-6 space-y-3">
                  {[
                    'R$4.000 a R$5.000',
                    'Processo demorado',
                    'Dependência de terceiros',
                    'Atendimento limitado',
                    'Sem acesso imediato',
                    'Informações fragmentadas',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <XCircle className="text-red-400 flex-shrink-0" size={20} />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            {/* E-Book */}
            <ScrollReveal delay={200}>
              <div className="rounded-2xl overflow-hidden h-full" style={{ border: '2px solid rgba(201,168,76,0.5)' }}>
                <div
                  className="px-6 py-4 text-center"
                  style={{ background: 'linear-gradient(135deg, #1e3060, #142444)' }}
                >
                  <h3 className="font-black text-xl uppercase tracking-wider gold-shimmer">E-Book Premium</h3>
                </div>
                <div className="p-6 space-y-3" style={{ background: 'linear-gradient(135deg, rgba(14,28,52,0.95), rgba(10,22,40,0.98))' }}>
                  {[
                    'Apenas R$49,90',
                    'Acesso imediato',
                    'Explicação simples e direta',
                    'Passo a passo completo',
                    'Links oficiais incluídos',
                    'Explicações detalhadas',
                    'Mais liberdade e autonomia',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="text-amber-400 flex-shrink-0" size={20} />
                      <span className="text-white text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Frase forte */}
          <ScrollReveal>
            <div className="text-center">
              <p
                className="text-xl md:text-2xl font-black text-white"
                style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}
              >
                "Esse guia entrega{' '}
                <span className="gold-shimmer">até mais informações práticas</span>{' '}
                do que muitas consultorias tradicionais."
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── SECTION: BENEFÍCIOS EMOCIONAIS ── */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#0a1628] to-[#0d1f3c]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <ScrollReveal>
            <div className="text-center mb-4">
              <span className="text-amber-400 text-xs font-bold tracking-[0.3em] uppercase">O Sonho é Real</span>
            </div>
            <h2
              className="text-center font-black mb-4 text-3xl md:text-5xl text-white"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Sua Vida Depois do <span className="gold-shimmer">Visto Aprovado</span>
            </h2>
            <div className="section-divider" />
            <p className="text-center text-gray-300 text-lg max-w-2xl mx-auto mb-12">
              Imagine a liberdade de cruzar as fronteiras americanas com total segurança e confiança.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((b, i) => (
              <ScrollReveal key={i} delay={i * 100}>
                <div className="relative rounded-2xl overflow-hidden group" style={{ minHeight: '260px' }}>
                  <img
                    src={b.img}
                    alt={b.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ filter: 'brightness(0.5) saturate(1.1)' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-transparent" />
                  <div className="relative z-10 p-6 flex flex-col justify-end h-full min-h-[260px]">
                    <h3 className="text-amber-300 font-black text-xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{b.title}</h3>
                    <p className="text-gray-200 text-sm leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION: EBOOK MOCKUP + SEAL ── */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#0d1f3c] to-[#0a1628]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
            <ScrollReveal className="flex-shrink-0">
              <div className="relative">
                <div
                  className="w-56 md:w-72 rounded-2xl overflow-hidden animate-float"
                  style={{
                    boxShadow: '0 0 60px rgba(201,168,76,0.4), 0 30px 60px rgba(0,0,0,0.6)',
                    border: '2px solid rgba(201,168,76,0.3)'
                  }}
                >
                  <img
                    src="https://static.lumi.new/0f/0fd2115509d5814175c3b44bdfa66698.png"
                    alt="O Guia Completo do Visto Americano"
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute -top-4 -right-4">
                  <GoldSeal />
                </div>
              </div>
            </ScrollReveal>

            <div className="flex-1">
              <ScrollReveal>
                <span className="text-amber-400 text-xs font-bold tracking-[0.3em] uppercase mb-4 block">O Material</span>
                <h2
                  className="font-black text-3xl md:text-4xl text-white mb-4"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  O Guia Completo do{' '}
                  <span className="gold-shimmer">Visto Americano</span>
                </h2>
                <p className="text-gray-300 text-base leading-relaxed mb-6">
                  Um material premium, desenvolvido especialmente para brasileiros que querem tirar o visto americano com segurança, sem depender de consultoria cara e sem precisar saber inglês.
                </p>
                <div className="space-y-3 mb-8">
                  {[
                    'Linguagem simples e direta para iniciantes',
                    'Links oficiais do governo americano',
                    'Scripts prontos para a entrevista',
                    'Checklist completo de documentos',
                    'Dicas exclusivas de quem já foi aprovado',
                    'Acesso imediato após o pagamento',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.2)', border: '1px solid rgba(201,168,76,0.5)' }}>
                        <CheckCircle size={12} className="text-amber-400" />
                      </div>
                      <span className="text-gray-200 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <button
                  className="btn-gold rounded-xl text-base font-black px-8 py-4 uppercase tracking-wider"
                  onClick={handleCheckout}
                >
                  QUERO GARANTIR MEU GUIA AGORA →
                </button>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION: DEPOIMENTOS ── */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#0a1628] to-[#0d1f3c]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <ScrollReveal>
            <div className="text-center mb-4">
              <span className="text-amber-400 text-xs font-bold tracking-[0.3em] uppercase">Quem Já Conquistou</span>
            </div>
            <h2
              className="text-center font-black mb-4 text-3xl md:text-5xl text-white"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Histórias de <span className="gold-shimmer">Sucesso</span>
            </h2>
            <div className="section-divider" />
            <p className="text-center text-gray-300 text-lg max-w-xl mx-auto mb-12">
              Brasileiros de todo o país que conquistaram o visto americano usando este guia.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <ScrollReveal key={i} delay={i * 80}>
                <div className="card-premium rounded-2xl p-6 h-full flex flex-col">
                  <div className="flex text-amber-400 mb-4 gap-0.5">
                    {Array.from({ length: t.stars }).map((_, si) => (
                      <Star key={si} size={16} fill="currentColor" />
                    ))}
                  </div>
                  <p className="text-gray-200 text-sm leading-relaxed italic flex-1 mb-5">"{t.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-700">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black text-amber-900 flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #c9a84c, #f5d98b)' }}
                    >
                      {t.initials}
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">{t.name}</p>
                      <p className="text-gray-500 text-xs">{t.city}</p>
                    </div>
                    <div className="ml-auto">
                      <div className="bg-green-900/40 border border-green-500/30 rounded-full px-2 py-0.5">
                        <span className="text-green-400 text-xs font-bold">✓ Aprovado</span>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION: GARANTIA ── */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#0d1f3c] to-[#0a1628]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <ScrollReveal>
            <div
              className="rounded-3xl p-8 md:p-12 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(14,28,52,0.95), rgba(10,22,40,0.98))',
                border: '2px solid rgba(201,168,76,0.4)',
                boxShadow: '0 0 60px rgba(201,168,76,0.1)',
              }}
            >
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(201,168,76,0.2), rgba(201,168,76,0.05))',
                  border: '3px solid rgba(201,168,76,0.5)',
                  boxShadow: '0 0 30px rgba(201,168,76,0.3)'
                }}
              >
                <Shield className="text-amber-400" size={44} />
              </div>
              <p className="text-amber-400 text-xs font-bold tracking-[0.3em] uppercase mb-3">Sem Riscos</p>
              <h2
                className="font-black text-3xl md:text-4xl text-white mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Garantia de{' '}
                <span className="gold-shimmer">7 Dias</span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed max-w-xl mx-auto mb-6">
                Se você comprar o guia e <strong className="text-white">não gostar do material</strong> por qualquer motivo, basta nos enviar uma mensagem em até 7 dias e{' '}
                <strong className="text-amber-300">devolvemos seu dinheiro integralmente</strong>. Sem perguntas. Sem burocracia.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Lock size={14} className="text-amber-400" />
                  <span>Compra 100% segura</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap size={14} className="text-amber-400" />
                  <span>Acesso imediato</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award size={14} className="text-amber-400" />
                  <span>Garantia total</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── SECTION: CTA FINAL ── */}
      <section id="cta-final" className="py-24 md:py-36 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.lumi.new/7b/7b190a6fc8929522b02203dd7e606ea0.png"
            alt="New York skyline"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.25) saturate(1.2)' }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, rgba(10,22,40,0.9) 0%, rgba(10,22,40,0.6) 50%, rgba(10,22,40,0.95) 100%)' }}
          />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(201,168,76,0.08) 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <ScrollReveal>
            <div className="text-4xl mb-6">🗽</div>
            <h2
              className="font-black text-3xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display, serif', textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
            >
              O sonho americano pode estar{' '}
              <span className="gold-shimmer">muito mais perto</span>{' '}
              do que você imagina.
            </h2>
            <p className="text-gray-200 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl mx-auto">
              Pare de perder tempo com informações confusas. Tenha acesso ao guia completo e{' '}
              <strong className="text-white">aprenda exatamente o que fazer</strong> em cada etapa.
            </p>

            {/* Final Price Box */}
            <div
              className="inline-block mb-8 rounded-2xl px-8 py-6 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(10,22,40,0.95) 0%, rgba(20,36,68,0.95) 100%)',
                border: '2px solid #c9a84c',
                boxShadow: '0 0 50px rgba(201,168,76,0.3)',
              }}
            >
              <p className="text-gray-400 text-sm line-through mb-1">De R$297,00</p>
              <p className="font-black text-4xl md:text-5xl mb-2" style={{ color: '#f5d98b', textShadow: '0 0 30px rgba(245,217,139,0.6)' }}>
                R$49,90
              </p>
              <p className="text-green-400 text-sm font-bold">✓ Acesso imediato · ✓ Garantia de 7 dias</p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <button
                className="btn-gold rounded-2xl text-xl md:text-2xl font-black px-10 md:px-16 py-6 md:py-7 uppercase tracking-wider w-full sm:w-auto animate-pulse-gold"
                onClick={handleCheckout}
              >
                🚀 QUERO MEU GUIA POR R$49,90
              </button>
              <p className="text-gray-400 text-sm flex items-center gap-2">
                <Lock size={14} className="text-amber-400" />
                Pagamento seguro via Pix, Cartão ou Boleto
              </p>
            </div>

            {/* Urgency */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <Clock className="text-red-400" size={16} />
                <span>Oferta por <strong className="text-red-400">tempo limitado</strong></span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <TrendingUp className="text-amber-400" size={16} />
                <span><strong className="text-amber-400">+4.800</strong> brasileiros já conquistaram o visto</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Users className="text-green-400" size={16} />
                <span><strong className="text-green-400">12 pessoas</strong> comprando agora</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── BOTÃO FLUTUANTE WHATSAPP ── */}
      <div
        className="fixed z-50 flex items-center gap-2.5"
        style={{
          right: 'clamp(16px, 4vw, 28px)',
          bottom: 'clamp(16px, 4vw, 28px)',
        }}
      >
        {/* Balão de texto sutil */}
        <div
          className="hidden sm:flex flex-col items-end leading-tight px-4 py-2 rounded-2xl rounded-br-sm animate-float"
          style={{
            background: 'rgba(255,255,255,0.96)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            maxWidth: '180px',
          }}
        >
          <span className="text-[#0a1628] text-xs font-bold">Converse com nossa equipe</span>
          <span className="text-gray-500 text-[11px]">ou retire suas dúvidas</span>
        </div>

      <a
        href="https://api.whatsapp.com/send?phone=5527992458381&text=Quero%20falar%20com%20o%20especislista%20sobre%20a%20consultoria.%20%F0%9F%9B%AB"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Falar no WhatsApp"
        className="relative flex items-center justify-center flex-shrink-0 group"
        style={{
          width: 'clamp(56px, 14vw, 64px)',
          height: 'clamp(56px, 14vw, 64px)',
          borderRadius: '9999px',
          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          boxShadow: '0 6px 20px rgba(37,211,102,0.5), 0 2px 8px rgba(0,0,0,0.4)',
        }}
      >
        {/* Pulso animado */}
        <span
          className="absolute inset-0 rounded-full"
          style={{
            background: 'rgba(37,211,102,0.5)',
            animation: 'whatsPulse 2s ease-out infinite',
          }}
        />
        <svg
          viewBox="0 0 32 32"
          fill="#ffffff"
          className="relative z-10"
          style={{ width: 'clamp(30px, 7.5vw, 36px)', height: 'clamp(30px, 7.5vw, 36px)' }}
        >
          <path d="M16.001 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.6 4.46 1.73 6.4L3.2 28.8l6.57-1.72a12.74 12.74 0 0 0 6.23 1.6h.01c7.06 0 12.8-5.74 12.8-12.8 0-3.42-1.33-6.63-3.75-9.05A12.7 12.7 0 0 0 16.001 3.2zm0 23.36h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-4 1.05 1.07-3.9-.25-.4a10.56 10.56 0 0 1-1.62-5.6c0-5.86 4.77-10.63 10.63-10.63 2.84 0 5.5 1.11 7.51 3.12a10.55 10.55 0 0 1 3.11 7.52c0 5.86-4.77 10.62-10.63 10.62zm5.83-7.96c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.71.16-.21.32-.82 1.04-1 1.25-.18.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.18-.32-.02-.49.14-.65.14-.14.32-.37.48-.55.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.71-1.72-.98-2.35-.26-.62-.52-.54-.71-.55l-.61-.01c-.21 0-.55.08-.84.4-.29.32-1.1 1.08-1.1 2.63 0 1.55 1.13 3.05 1.29 3.26.16.21 2.22 3.39 5.38 4.76.75.32 1.34.52 1.8.66.76.24 1.44.21 1.98.13.6-.09 1.89-.77 2.16-1.52.27-.74.27-1.38.19-1.51-.08-.13-.29-.21-.61-.37z"/>
        </svg>
      </a>
      </div>

      {/* ── FOOTER ── */}
      <footer className="py-8 bg-[#060f1e] border-t border-gray-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex justify-center mb-3 text-2xl gap-2">
            🇺🇸 🗽 ✈️
          </div>
          <p className="text-gray-500 text-sm font-semibold mb-1">O Guia Completo do Visto Americano</p>
          <p className="text-gray-600 text-xs">
            © {new Date().getFullYear()} Landing Visto EUA · Todos os direitos reservados ·{' '}
            <span className="text-amber-600">Garantia de 7 dias</span> ·{' '}
            Material educativo · Informações sujeitas a alterações do governo americano
          </p>
          <p className="text-gray-700 text-xs mt-2">
            Os depoimentos apresentados são ilustrativos dos resultados que os usuários do guia alcançaram.
          </p>
        </div>
      </footer>
    </div>
  )
}
